import React, { useState } from "react";
import {
  useAmazonClaims,
  useAmazonClaimStats,
  useReviewAmazonClaim,
  useAmazonConfig,
  useUpdateAmazonConfig,
  useUpdateOcrAmount,
  useTriggerAutoApprove,
} from "../hooks/useAmazonClaims";
import { useLocalStorage } from "../hooks/useLocalStorage";
import SearchBar from "../components/common/SearchBar";
import LoadingSpinner from "../components/common/LoadingSpinner";
import Pagination from "../components/common/Pagination";
import { AmazonClaim, AmazonClaimStatus } from "../services/api";
import toast from "react-hot-toast";
import BulkImportModal from "../components/amazon/BulkImportModal";
import {
  FunnelIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  ExclamationTriangleIcon,
  CurrencyEuroIcon,
  ShieldExclamationIcon,
  Cog6ToothIcon,
  ArrowUpTrayIcon,
} from "@heroicons/react/24/outline";

type FilterStatus = "all" | AmazonClaimStatus;

const AmazonClaims: React.FC = () => {
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedClaim, setSelectedClaim] = useState<AmazonClaim | null>(null);
  const [isRejectModalOpen, setIsRejectModalOpen] = useState(false);
  const [rejectReason, setRejectReason] = useState("");

  const [reviewNote, setReviewNote] = useState("");
  const [approvedAmount, setApprovedAmount] = useState("");
  const [commissionAmount, setCommissionAmount] = useState("");
  const [isScreenshotOpen, setIsScreenshotOpen] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);
  const [isEditingOcrAmount, setIsEditingOcrAmount] = useState(false);
  const [editedOcrAmount, setEditedOcrAmount] = useState("");
  const [filter, setFilter] = useLocalStorage<FilterStatus>(
    "amazon-claims-filter",
    "all",
  );
  const [searchQuery, setSearchQuery] = useLocalStorage<string>(
    "amazon-claims-search",
    "",
  );
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(20);

  // Amazon Config State
  const [isConfigEditing, setIsConfigEditing] = useState(false);
  const [newCashbackPercent, setNewCashbackPercent] = useState("");

  const { data: amazonConfig, isLoading: isConfigLoading } = useAmazonConfig();
  const updateConfigMutation = useUpdateAmazonConfig();

  const { data: claimsResponse, isLoading } = useAmazonClaims({
    limit: itemsPerPage,
    skip: (currentPage - 1) * itemsPerPage,
    customerDisplayId: searchQuery || undefined,
    status: filter !== "all" ? filter : undefined,
  });

  const { data: stats } = useAmazonClaimStats();

  const reviewMutation = useReviewAmazonClaim();
  const updateOcrAmountMutation = useUpdateOcrAmount();
  const triggerAutoApproveMutation = useTriggerAutoApprove();

  const claims = claimsResponse?.claims || [];
  const totalItems = claimsResponse?.total || 0;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Reset page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchQuery]);

  const handleViewDetails = (claim: AmazonClaim) => {
    setSelectedClaim(claim);
    setApprovedAmount(
      claim.customerEdited
        ? (claim.customerAmount ?? claim.ocrAmount).toString()
        : claim.ocrAmount.toString(),
    );
    // Always recalculate commission using the current cashback rate from config
    // (avoids showing stale values when cashback % was changed after upload)
    const effectiveAmount = claim.customerEdited
      ? (claim.customerAmount ?? claim.ocrAmount)
      : claim.ocrAmount;
    const currentCashbackPercent = amazonConfig?.cashbackPercent ?? 4.5;
    const recalculatedCommission =
      effectiveAmount > 0
        ? (effectiveAmount * currentCashbackPercent) / 100
        : (claim.commissionAmount ?? 0);
    setCommissionAmount(recalculatedCommission.toFixed(2));
    setReviewNote("");
    setIsEditingOcrAmount(false);
    setEditedOcrAmount("");
    setIsDetailModalOpen(true);
  };

  const handleApprove = (claim: AmazonClaim) => {
    const commission = parseFloat(commissionAmount);
    if (!commission || commission <= 0) {
      toast.error("Commission amount (EUR) is required for approval");
      return;
    }
    const amount = parseFloat(approvedAmount) || claim.ocrAmount;
    reviewMutation.mutate(
      {
        claimId: claim._id,
        data: {
          action: "approved",
          approvedAmount: amount,
          approvedCurrency: claim.ocrCurrency || "EUR",
          commissionAmount: commission,
          adminNote: reviewNote || undefined,
        },
      },
      {
        onSuccess: () => {
          toast.success("Claim approved");
          setIsDetailModalOpen(false);
          setSelectedClaim(null);
        },
        onError: (error: any) => {
          toast.error(
            error.response?.data?.message || "Failed to approve claim",
          );
        },
      },
    );
  };

  const handleRejectSingle = (claim: AmazonClaim) => {
    setSelectedClaim(claim);
    setRejectReason("");
    setIsRejectModalOpen(true);
  };

  const handleReject = () => {
    if (!rejectReason.trim()) return;

    if (selectedClaim) {
      reviewMutation.mutate(
        {
          claimId: selectedClaim._id,
          data: {
            action: "rejected",
            rejectionReason: rejectReason,
          },
        },
        {
          onSuccess: () => {
            toast.success("Claim rejected");
            setIsRejectModalOpen(false);
            setIsDetailModalOpen(false);
            setSelectedClaim(null);
          },
          onError: (error: any) => {
            toast.error(
              error.response?.data?.message || "Failed to reject claim",
            );
          },
        },
      );
    }
  };

  const getStatusBadge = (status: AmazonClaimStatus) => {
    const badges: Record<
      AmazonClaimStatus,
      { bg: string; icon: React.ReactNode; label: string }
    > = {
      pending: {
        bg: "bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200",
        icon: <ClockIcon className="h-4 w-4 mr-1" />,
        label: "Pending",
      },
      under_review: {
        bg: "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200",
        icon: <EyeIcon className="h-4 w-4 mr-1" />,
        label: "Under Review",
      },
      approved: {
        bg: "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200",
        icon: <CheckCircleIcon className="h-4 w-4 mr-1" />,
        label: "Approved",
      },
      rejected: {
        bg: "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200",
        icon: <XCircleIcon className="h-4 w-4 mr-1" />,
        label: "Rejected",
      },
      paid: {
        bg: "bg-purple-100 dark:bg-purple-900 text-purple-800 dark:text-purple-200",
        icon: <CurrencyEuroIcon className="h-4 w-4 mr-1" />,
        label: "Paid",
      },
    };
    const badge = badges[status];
    if (!badge) return <span className="text-xs text-gray-500">{status}</span>;
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${badge.bg}`}
      >
        {badge.icon}
        {badge.label}
      </span>
    );
  };

  const getConfidenceBadge = (confidence: number) => {
    if (confidence >= 80) {
      return "bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200";
    } else if (confidence >= 50) {
      return "bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200";
    }
    return "bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200";
  };

  const formatCurrency = (amount: number, currency?: string) => {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency: currency || "EUR",
    }).format(amount);
  };

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Amazon
        </h1>
        <p className="text-gray-600 dark:text-gray-400">
          Review and manage Amazon affiliate earnings
        </p>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-3">
        <button
          onClick={() => {
            if (
              window.confirm(
                "Run auto-approve now? All pending claims older than 3 weeks with amount between 0–400 EUR will be approved automatically.",
              )
            ) {
              triggerAutoApproveMutation.mutate(undefined, {
                onSuccess: () =>
                  toast.success("Auto-approve completed successfully"),
                onError: () =>
                  toast.error("Auto-approve failed. Check server logs."),
              });
            }
          }}
          disabled={triggerAutoApproveMutation.isPending}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CheckCircleIcon className="h-4 w-4 mr-2" />
          {triggerAutoApproveMutation.isPending
            ? "Running..."
            : "Run Auto-Approve"}
        </button>
        <button
          onClick={() => setIsBulkImportOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
        >
          <ArrowUpTrayIcon className="h-4 w-4 mr-2" />
          Bulk Import
        </button>
      </div>

      {/* Amazon Config Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Cog6ToothIcon className="h-6 w-6 text-gray-500 dark:text-gray-400" />
            <div>
              <h2 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wider">
                Configuration
              </h2>
              <div className="mt-1 flex items-center space-x-2">
                <span className="text-2xl font-bold text-primary-600">
                  {isConfigLoading
                    ? "..."
                    : `${amazonConfig?.cashbackPercent}%`}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  Cashback Rate
                </span>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            {isConfigEditing ? (
              <>
                <div className="relative rounded-md shadow-sm w-24">
                  <input
                    type="number"
                    value={newCashbackPercent}
                    onChange={(e) => setNewCashbackPercent(e.target.value)}
                    className="block w-full rounded-md border-gray-300 dark:border-gray-600 pl-3 pr-8 focus:border-primary-500 focus:ring-primary-500 sm:text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    placeholder="4"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                    <span className="text-gray-500 sm:text-sm">%</span>
                  </div>
                </div>
                <button
                  onClick={() => {
                    const percent = parseFloat(newCashbackPercent);
                    if (isNaN(percent) || percent < 0 || percent > 100) {
                      toast.error("Please enter a valid percentage (0-100)");
                      return;
                    }
                    updateConfigMutation.mutate(percent, {
                      onSuccess: () => {
                        toast.success("Configuration updated");
                        setIsConfigEditing(false);
                      },
                      onError: (error: any) => {
                        toast.error(
                          error.response?.data?.message || "Update failed",
                        );
                      },
                    });
                  }}
                  disabled={updateConfigMutation.isPending}
                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  {updateConfigMutation.isPending ? "Saving..." : "Save"}
                </button>
                <button
                  onClick={() => setIsConfigEditing(false)}
                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => {
                  setNewCashbackPercent(
                    amazonConfig?.cashbackPercent?.toString() || "4",
                  );
                  setIsConfigEditing(true);
                }}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
              >
                Edit Configuration
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Total
            </div>
            <div className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.total || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Pending
            </div>
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {stats.pending || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Approved
            </div>
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {stats.approved || 0}
            </div>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              Rejected
            </div>
            <div className="text-2xl font-bold text-red-600 dark:text-red-400">
              {stats.rejected || 0}
            </div>
          </div>
        </div>
      )}

      {/* Filters and Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-3 lg:space-y-0 lg:space-x-4">
          <div className="flex-1 max-w-md">
            <SearchBar
              onSearch={setSearchQuery}
              initialValue={searchQuery}
              placeholder="Search by customer ID (e.g. E-12345)..."
            />
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex items-center space-x-2">
              <FunnelIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
              <select
                value={filter}
                onChange={(e) => setFilter(e.target.value as FilterStatus)}
                className="rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
              >
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="under_review">Under Review</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="paid">Paid</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Claims Table */}
      {isLoading ? (
        <div className="flex justify-center py-8">
          <LoadingSpinner size="lg" />
        </div>
      ) : claims.length > 0 ? (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Customer
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Cashback
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Est. EP
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Confidence
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Period
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Flags
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {claims.map((claim) => (
                  <tr
                    key={claim._id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="text-sm font-mono text-gray-900 dark:text-white">
                        {claim.customerDisplayId}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-semibold text-gray-900 dark:text-white">
                          {formatCurrency(claim.ocrAmount, claim.ocrCurrency)}
                        </div>
                        {claim.customerEdited && (
                          <div className="text-xs text-orange-600 dark:text-orange-400">
                            Edited:{" "}
                            {formatCurrency(
                              claim.customerAmount || 0,
                              claim.customerCurrency,
                            )}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {claim.commissionAmount != null ? (
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {formatCurrency(claim.commissionAmount)}
                          </div>
                          {claim.appliedCashbackPercent != null && (
                            <div className="text-[10px] text-gray-400">
                              {claim.appliedCashbackPercent}%
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {claim.estimatedRewardAmount != null ? (
                        <span className="text-sm font-medium text-primary-600 dark:text-primary-400">
                          {claim.estimatedRewardAmount.toLocaleString("de-DE")}{" "}
                          EP
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getConfidenceBadge(claim.ocrConfidence)}`}
                      >
                        {claim.ocrConfidence}%
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-white">
                      {claim.amazonPeriod || "-"}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col space-y-1">
                        {claim.isDuplicate && (
                          <span className="inline-flex items-center gap-1 text-xs text-orange-600 dark:text-orange-400 bg-orange-50 dark:bg-orange-900/20 px-2 py-0.5 rounded-full">
                            <ExclamationTriangleIcon className="h-3.5 w-3.5" />
                            Duplicate
                            {claim.duplicateReason
                              ? ` (${claim.duplicateReason.replace("_", " ")})`
                              : ""}
                          </span>
                        )}
                        {(claim.fraudScore || 0) > 50 && (
                          <span className="inline-flex items-center gap-1 text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 px-2 py-0.5 rounded-full">
                            <ShieldExclamationIcon className="h-3.5 w-3.5" />
                            Fraud {claim.fraudScore}/100
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      {getStatusBadge(claim.status)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(claim.createdAt)}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <button
                        onClick={() => handleViewDetails(claim)}
                        className="text-primary-600 dark:text-primary-400 hover:text-primary-900 dark:hover:text-primary-300"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              showPageJump
            />
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-8 text-center border border-gray-200 dark:border-gray-700">
          <div className="text-gray-400 dark:text-gray-500 mb-3">
            <DocumentTextIcon className="h-12 w-12 mx-auto" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            No claims found
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            {searchQuery || filter !== "all"
              ? "Try adjusting your search or filters"
              : "No Amazon claims submitted yet"}
          </p>
        </div>
      )}

      {/* Detail Modal */}
      {selectedClaim && isDetailModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsDetailModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-3xl sm:w-full">
              <div className="bg-primary-600 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <DocumentTextIcon className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="text-xl font-semibold text-white">
                      Claim Details
                    </h3>
                    <p className="text-sm text-primary-100">
                      {selectedClaim.customerDisplayId}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setIsDetailModalOpen(false)}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6 max-h-[calc(100vh-200px)] overflow-y-auto">
                <div className="space-y-6">
                  {/* Status */}
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                      Status
                    </span>
                    {getStatusBadge(selectedClaim.status)}
                  </div>

                  {/* Screenshot */}
                  {selectedClaim.screenshotUrl && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Screenshot
                      </h4>
                      <div
                        className="bg-gray-100 dark:bg-gray-700 rounded-lg p-2 max-h-48 overflow-hidden cursor-pointer relative group"
                        onClick={() => setIsScreenshotOpen(true)}
                      >
                        <img
                          src={selectedClaim.screenshotUrl}
                          alt="Amazon earnings screenshot"
                          className="max-w-full h-auto rounded"
                          onError={(e) => {
                            (e.target as HTMLImageElement).style.display =
                              "none";
                          }}
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors flex items-center justify-center">
                          <span className="opacity-0 group-hover:opacity-100 transition-opacity text-white bg-black/50 px-3 py-1 rounded text-sm">
                            Click to enlarge
                          </span>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* OCR Data */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      OCR Data
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          OCR Amount
                        </label>
                        {isEditingOcrAmount ? (
                          <div className="mt-1 flex items-center space-x-2">
                            <input
                              type="number"
                              step="0.01"
                              value={editedOcrAmount}
                              onChange={(e) =>
                                setEditedOcrAmount(e.target.value)
                              }
                              className="w-24 rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-1 text-sm"
                            />
                            <button
                              onClick={() => {
                                updateOcrAmountMutation.mutate(
                                  {
                                    claimId: selectedClaim._id,
                                    amount: parseFloat(editedOcrAmount),
                                  },
                                  {
                                    onSuccess: (updatedClaim: AmazonClaim) => {
                                      setSelectedClaim(updatedClaim);
                                      setApprovedAmount(
                                        updatedClaim.ocrAmount.toString(),
                                      );
                                      setIsEditingOcrAmount(false);
                                      toast.success("OCR Amount updated");
                                    },
                                    onError: () => {
                                      toast.error(
                                        "Failed to update OCR amount",
                                      );
                                    },
                                  },
                                );
                              }}
                              disabled={updateOcrAmountMutation.isPending}
                              className="text-green-600 hover:text-green-700 disabled:opacity-50"
                              title="Save"
                            >
                              <CheckCircleIcon className="h-5 w-5" />
                            </button>
                            <button
                              onClick={() => setIsEditingOcrAmount(false)}
                              disabled={updateOcrAmountMutation.isPending}
                              className="text-gray-400 hover:text-gray-500 disabled:opacity-50"
                              title="Cancel"
                            >
                              <XCircleIcon className="h-5 w-5" />
                            </button>
                          </div>
                        ) : (
                          <div className="mt-1 flex items-center space-x-2 group">
                            <p className="text-2xl font-bold text-gray-900 dark:text-white">
                              {formatCurrency(
                                selectedClaim.ocrAmount,
                                selectedClaim.ocrCurrency,
                              )}
                            </p>
                            <button
                              onClick={() => {
                                setEditedOcrAmount(
                                  selectedClaim.ocrAmount.toString(),
                                );
                                setIsEditingOcrAmount(true);
                              }}
                              className="opacity-0 group-hover:opacity-100 transition-opacity text-gray-400 hover:text-primary-500"
                              title="Edit OCR Amount"
                            >
                              <svg
                                className="h-4 w-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                />
                              </svg>
                            </button>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Confidence
                        </label>
                        <p className="mt-1">
                          <span
                            className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${getConfidenceBadge(selectedClaim.ocrConfidence)}`}
                          >
                            {selectedClaim.ocrConfidence}%
                          </span>
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Period
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedClaim.amazonPeriod || "-"}
                        </p>
                      </div>
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Provider
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {selectedClaim.ocrProvider}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Customer Edits */}
                  {selectedClaim.customerEdited && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-orange-600 dark:text-orange-400 mb-3">
                        Customer Corrections
                      </h4>
                      <div className="bg-orange-50 dark:bg-orange-900/20 rounded-lg p-4">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Customer Amount
                            </label>
                            <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                              {formatCurrency(
                                selectedClaim.customerAmount || 0,
                                selectedClaim.customerCurrency,
                              )}
                            </p>
                          </div>
                          {selectedClaim.customerNote && (
                            <div>
                              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Customer Note
                              </label>
                              <p className="mt-1 text-sm text-gray-900 dark:text-white">
                                {selectedClaim.customerNote}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Fraud Detection */}
                  {((selectedClaim.fraudScore || 0) > 0 ||
                    selectedClaim.isDuplicate) && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Fraud Detection
                      </h4>
                      <div
                        className={`rounded-lg p-4 ${
                          (selectedClaim.fraudScore || 0) > 50
                            ? "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700"
                            : "bg-gray-50 dark:bg-gray-700"
                        }`}
                      >
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Fraud Score
                            </label>
                            <p
                              className={`mt-1 text-lg font-semibold ${
                                (selectedClaim.fraudScore || 0) > 50
                                  ? "text-red-600 dark:text-red-400"
                                  : "text-gray-900 dark:text-white"
                              }`}
                            >
                              {selectedClaim.fraudScore || 0}/100
                            </p>
                          </div>
                          <div>
                            <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                              Duplicate
                            </label>
                            <p className="mt-1 text-sm text-gray-900 dark:text-white">
                              {selectedClaim.isDuplicate ? (
                                <span className="text-orange-600 dark:text-orange-400 font-medium">
                                  Yes - {selectedClaim.duplicateReason}
                                </span>
                              ) : (
                                "No"
                              )}
                            </p>
                          </div>
                        </div>
                        {selectedClaim.fraudReasons &&
                          selectedClaim.fraudReasons.length > 0 && (
                            <div className="mt-3">
                              <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                                Fraud Reasons
                              </label>
                              <ul className="mt-1 list-disc list-inside text-sm text-red-700 dark:text-red-300">
                                {selectedClaim.fraudReasons.map((reason, i) => (
                                  <li key={i}>{reason}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                      </div>
                    </div>
                  )}

                  {/* Rejection Reason */}
                  {selectedClaim.rejectionReason && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700 rounded-lg p-4">
                      <div className="flex items-center space-x-2 mb-2">
                        <XCircleIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
                        <span className="font-medium text-red-800 dark:text-red-200">
                          Rejection Reason
                        </span>
                      </div>
                      <p className="text-sm text-red-700 dark:text-red-300">
                        {selectedClaim.rejectionReason}
                      </p>
                    </div>
                  )}

                  {/* Admin Note */}
                  {selectedClaim.adminNote && (
                    <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
                      <span className="text-xs font-medium text-blue-600 dark:text-blue-400 uppercase">
                        Admin Note
                      </span>
                      <p className="mt-1 text-sm text-blue-800 dark:text-blue-200">
                        {selectedClaim.adminNote}
                      </p>
                    </div>
                  )}

                  {/* Timestamps */}
                  <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                      Timestamps
                    </h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Submitted
                        </label>
                        <p className="mt-1 text-sm text-gray-900 dark:text-white">
                          {formatDate(selectedClaim.createdAt)}
                        </p>
                      </div>
                      {selectedClaim.reviewedAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Reviewed
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {formatDate(selectedClaim.reviewedAt)}
                          </p>
                        </div>
                      )}
                      {selectedClaim.paidAt && (
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Paid
                          </label>
                          <p className="mt-1 text-sm text-gray-900 dark:text-white">
                            {formatDate(selectedClaim.paidAt)}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Cashback Estimates */}
                  {(selectedClaim.commissionAmount != null ||
                    selectedClaim.estimatedRewardAmount != null) && (
                    <div className="border-b border-gray-200 dark:border-gray-700 pb-4">
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Cashback Estimates
                      </h4>
                      <div className="grid grid-cols-3 gap-4 bg-primary-50 dark:bg-primary-900/20 rounded-lg p-4">
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Cashback Rate
                          </label>
                          <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                            {selectedClaim.appliedCashbackPercent != null
                              ? `${selectedClaim.appliedCashbackPercent}%`
                              : "-"}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Commission (EUR)
                          </label>
                          <p className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                            {selectedClaim.commissionAmount != null
                              ? formatCurrency(selectedClaim.commissionAmount)
                              : "-"}
                          </p>
                        </div>
                        <div>
                          <label className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                            Est. Reward
                          </label>
                          <p className="mt-1 text-lg font-semibold text-primary-600 dark:text-primary-400">
                            {selectedClaim.estimatedRewardAmount != null
                              ? `${selectedClaim.estimatedRewardAmount.toLocaleString("de-DE")} EP`
                              : "-"}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Review Actions */}
                  {(selectedClaim.status === "pending" ||
                    selectedClaim.status === "under_review") && (
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                        Review
                      </h4>
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Approved Amount
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={approvedAmount}
                            onChange={(e) => setApprovedAmount(e.target.value)}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Commission from Amazon (EUR)
                          </label>
                          <input
                            type="number"
                            step="0.01"
                            value={commissionAmount}
                            onChange={(e) =>
                              setCommissionAmount(e.target.value)
                            }
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                            placeholder="e.g. 10.00"
                          />
                          {commissionAmount &&
                            parseFloat(commissionAmount) > 0 && (
                              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                ={" "}
                                {Math.round(
                                  parseFloat(commissionAmount) * 0.8 * 100,
                                ).toLocaleString("de-DE")}{" "}
                                EP for customer
                              </p>
                            )}
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                            Admin Note (optional)
                          </label>
                          <textarea
                            value={reviewNote}
                            onChange={(e) => setReviewNote(e.target.value)}
                            rows={2}
                            className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white p-2"
                            placeholder="Optional note..."
                          />
                        </div>
                        <div className="flex space-x-3">
                          <button
                            onClick={() => handleApprove(selectedClaim)}
                            disabled={reviewMutation.isPending}
                            className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <CheckCircleIcon className="h-4 w-4 mr-2" />
                            {reviewMutation.isPending
                              ? "Processing..."
                              : "Approve"}
                          </button>
                          <button
                            onClick={() => handleRejectSingle(selectedClaim)}
                            disabled={reviewMutation.isPending}
                            className="flex-1 inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            <XCircleIcon className="h-4 w-4 mr-2" />
                            Reject
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {isRejectModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              onClick={() => setIsRejectModalOpen(false)}
            >
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-red-600 px-6 py-4">
                <h3 className="text-xl font-semibold text-white">
                  Reject Claim
                </h3>
                <p className="text-sm text-red-100">
                  Claim from {selectedClaim?.customerDisplayId}
                </p>
              </div>

              <div className="bg-white dark:bg-gray-800 px-6 py-6">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Rejection Reason *
                </label>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  rows={4}
                  className="w-full rounded-md border-gray-300 dark:border-gray-600 shadow-sm focus:border-primary-500 focus:ring-primary-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  placeholder="Enter the reason for rejection..."
                />
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-6 py-4 flex justify-end space-x-3">
                <button
                  onClick={() => setIsRejectModalOpen(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600"
                >
                  Cancel
                </button>
                <button
                  onClick={handleReject}
                  disabled={!rejectReason.trim() || reviewMutation.isPending}
                  className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {reviewMutation.isPending ? "Rejecting..." : "Reject"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Screenshot Lightbox Modal */}
      {isScreenshotOpen && selectedClaim?.screenshotUrl && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/80 cursor-pointer"
          onClick={() => setIsScreenshotOpen(false)}
        >
          <button
            onClick={() => setIsScreenshotOpen(false)}
            className="absolute top-4 right-4 text-white hover:text-gray-300 text-3xl font-bold z-10"
          >
            &times;
          </button>
          <img
            src={selectedClaim.screenshotUrl}
            alt="Amazon earnings screenshot"
            className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
      {/* Bulk Import Modal */}
      <BulkImportModal
        isOpen={isBulkImportOpen}
        onClose={() => setIsBulkImportOpen(false)}
      />
    </div>
  );
};

export default AmazonClaims;
